import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { templateSettingsSchema, type TemplateSettingsSchemaT } from "@patient-watch/types";
import { ui } from "@patient-watch/ui";
import { Button } from "@patient-watch/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@patient-watch/ui/tooltip";
import { Info } from "lucide-react";
import { useSelectStaff } from "@/features/profile/api/selectStaff";
import { useSelectForms } from "@/features/forms/api/selectForms";
import { useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import { useSelectSurgeryOptions } from "@/features/events/features/surgeries/api/selectSurgeryOptions";
import { useInjectionMethodsQuery } from "@/features/injection/api/selectInjectionMethods";
import useInjectionOptions from "@/features/injection/hooks/useInjectionOptions";
import { useDiarySummaryValidation } from "@/features/templates/hooks/useDiarySummaryValidation";
import { deriveUpdateMode } from "@/features/templates/hooks/deriveUpdateMode";
import { services as s } from "@/services";
import { components as c } from "@/components";
import { ContactPersonAccordion } from "@/features/templates/components/diary-summary/ContactPersonAccordion";
import { EducationAccordion } from "@/features/templates/components/diary-summary/EducationAccordion";
import { InjectionAccordion } from "@/features/templates/components/diary-summary/InjectionAccordion";
import { InjectionDetailsAccordion } from "@/features/templates/components/diary-summary/InjectionDetailsAccordion";
import { ShockwaveAccordion } from "@/features/templates/components/diary-summary/ShockwaveAccordion";
import { ShockwaveDetailsAccordion } from "@/features/templates/components/diary-summary/ShockwaveDetailsAccordion";
import { SurgeryAccordion } from "@/features/templates/components/diary-summary/SurgeryAccordion";
import { SurgeryDetailsAccordion } from "@/features/templates/components/diary-summary/SurgeryDetailsAccordion";
import { TrackersAccordion } from "@/features/templates/components/diary-summary/TrackersAccordion";
import { DiarySummaryAlerts } from "@/features/templates/components/diary-summary/DiarySummaryAlerts";
import { useDiarySummarySync } from "@/features/templates/hooks/useDiarySummarySync";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";

export const Route = createFileRoute("/_app/patients/$diaryId/update/review")({
    component: UpdateDiaryReviewPage,
});

function UpdateDiaryReviewPage() {
    const copy = usePatientsCopy();
    const { diaryId } = Route.useParams();
    const search = Route.useSearch() as TemplateSettingsSchemaT;
    const navigate = useNavigate();

    const { auth } = Route.useRouteContext();
    const isAdmin = auth?.roles?.isAdmin || auth?.isSuperUser;

    const { data: staffData = [] } = useSelectStaff();
    const { data: formsData = [] } = useSelectForms();
    const { data: schedulesData = [] } = useSchedulesQuery();
    const { data: injectionMethods = [] } = useInjectionMethodsQuery();
    const { data: surgeryOptions = [] } = useSelectSurgeryOptions();

    const form = useForm<TemplateSettingsSchemaT>({
        resolver: zodResolver(templateSettingsSchema),
        defaultValues: {
            ...search,
            quickstart_key: search.quickstart_key || "PAIN",
        },
    });

    useDiarySummarySync(form, diaryId);

    const formData = form.watch();
    const mode = deriveUpdateMode(formData);
    const validation = useDiarySummaryValidation(formData, mode);
    const { methodOptions, clinicianOptions } = useInjectionOptions(undefined);

    const trackers = formData.trackers || [];
    const injection_details = formData.injection_details || [];
    const shockwave_details = formData.shockwave_details || [];

    const handleConfirm = () => {
        const data = form.getValues();
        void navigate({
            to: "/patients/$diaryId/update/existing-trackers",
            params: { diaryId },
            search: { ...data, type: mode },
        });
    };

    return (
        <TooltipProvider>
            <div className="flex flex-col gap-4">
                <div className="flex items-start gap-2">
                    <c.cc.Title words={copy.reviewTemplate} description={copy.reviewTemplateDescription} />
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className="mt-1 text-muted-foreground hover:text-foreground transition-colors shrink-0"
                                aria-label="Help for edge cases"
                            >
                                <Info size={16} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-sm" side="right">
                            <p className="mb-1 font-medium">Edge case guidance</p>
                            <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                                <li>Bilateral: add the same template twice, once per side.</li>
                                <li>
                                    Revision: add this template - the new procedure replaces the prior one in context.
                                </li>
                                <li>Multi-condition: each condition should have its own diary.</li>
                            </ul>
                        </TooltipContent>
                    </Tooltip>
                </div>

                <DiarySummaryAlerts incompleteSections={validation.incompleteSections} />

                <ui.Form {...form}>
                    <form className="space-y-4 pb-8">
                        <ui.Accordion className="space-y-2" type="single" collapsible>
                            {mode !== "intervention" && (
                                <TrackersAccordion
                                    control={form.control}
                                    trackers={trackers}
                                    forms={formsData as any}
                                    schedules={schedulesData as any}
                                    isComplete={validation.isSectionComplete("Trackers")}
                                    isAdmin={isAdmin}
                                />
                            )}

                            {formData.required_events?.includes("injection") && (
                                <>
                                    <InjectionAccordion
                                        staff={staffData as any}
                                        injectionMethods={injectionMethods}
                                        referringClinicianId={formData.referring_clinician_id ?? undefined}
                                        injectingClinicianId={formData.injecting_clinician_id ?? undefined}
                                        injectionMethodId={formData.injection_method_id ?? undefined}
                                        clinicianOptions={clinicianOptions}
                                        methodOptions={methodOptions}
                                        isComplete={validation.isSectionComplete("Injection")}
                                    />
                                    <InjectionDetailsAccordion
                                        injectionDetails={injection_details}
                                        injectionId={search.injection_id ?? s.uuidv4()}
                                        isComplete={validation.isSectionComplete("Injection Details")}
                                    />
                                </>
                            )}

                            {formData.required_events?.includes("surgery") && (
                                <>
                                    <SurgeryAccordion
                                        staff={staffData as any}
                                        referringClinicianId={formData.referring_clinician_id ?? undefined}
                                        operatingClinicianId={formData.operating_clinician_id ?? undefined}
                                        isComplete={validation.isSectionComplete("Surgery")}
                                    />
                                    <SurgeryDetailsAccordion
                                        side={formData.side ?? undefined}
                                        orthopaedicArea={formData.orthopaedic_area ?? undefined}
                                        surgeryOptionId={formData.surgery_option_id ?? undefined}
                                        surgeryOptions={surgeryOptions}
                                        isComplete={validation.isSectionComplete("Surgery Details")}
                                    />
                                </>
                            )}

                            {formData.required_events?.includes("education") && (
                                <EducationAccordion
                                    staff={staffData as any}
                                    educationName={formData.education_name ?? undefined}
                                    educatedBy={formData.educated_by ?? undefined}
                                    clinicianOptions={clinicianOptions}
                                    isComplete={validation.isSectionComplete("Consultation")}
                                />
                            )}

                            <ContactPersonAccordion
                                staff={staffData as any}
                                contactId={formData.contact_id ?? undefined}
                                isComplete={validation.isSectionComplete("Contact Person")}
                            />

                            {formData.required_events?.includes("shockwave") && (
                                <>
                                    <ShockwaveAccordion
                                        staff={staffData as any}
                                        referringClinicianId={formData.referring_clinician_id ?? undefined}
                                        shockwavingClinicianId={formData.shockwaving_clinician_id ?? undefined}
                                        isComplete={validation.isSectionComplete("Shockwave")}
                                    />
                                    <ShockwaveDetailsAccordion
                                        shockwaveDetails={shockwave_details}
                                        shockwaveId={search.shockwave_id ?? s.uuidv4()}
                                        isComplete={validation.isSectionComplete("Shockwave Details")}
                                    />
                                </>
                            )}
                        </ui.Accordion>

                        <Button size="lg" className="w-full" type="button" onClick={handleConfirm}>
                            {copy.confirmAndContinue}
                        </Button>
                    </form>
                </ui.Form>
            </div>
        </TooltipProvider>
    );
}
